// =========================================================================
// HelpyDoc · Actualización automática de la aplicación web.
//
// EL PROBLEMA. Cada despliegue publicaba una versión nueva que el paciente solo
// recibía si cerraba la app y volvía a entrar. Una app de salud abierta durante
// días podía quedarse semanas con una versión antigua —incluida una con un
// fallo ya corregido— sin que nadie se enterara.
//
// CÓMO FUNCIONA. El build incrusta su `__BUILD_ID__` y publica el mismo id en
// `version.json`. La app compara cada cierto tiempo: si el del servidor es
// distinto, hay despliegue nuevo. Los dos valores salen del MISMO build (ver
// `vite.config.ts`), porque calcularlos por separado provocaría un bucle de
// recargas.
//
// CUÁNDO SE APLICA, y por qué así. Recargar en mitad de algo es hostil: puede
// tirar una foto a medio analizar o un formulario a medio rellenar. Por eso la
// recarga se hace al VOLVER A PRIMER PLANO, que es el momento natural en que el
// paciente no está haciendo nada, y solo si quien nos usa no ha declarado
// trabajo en curso (`marcarTrabajoEnCurso`). Si lo hay, se espera: la próxima
// vuelta a primer plano lo intentará otra vez.
// =========================================================================

declare const __BUILD_ID__: string;

/** Cada cuánto se pregunta por una versión nueva estando la app en uso. */
const INTERVALO_MS = 15 * 60 * 1000;

let _trabajoEnCurso = 0;
let _iniciado = false;

/**
 * Declara que hay algo en marcha que una recarga destruiría (una foto
 * subiéndose, un análisis, un formulario a medio guardar). Devuelve la función
 * que lo da por terminado. Anidable: solo cuando todo termina se puede recargar.
 */
export function marcarTrabajoEnCurso(): () => void {
  _trabajoEnCurso += 1;
  let liberado = false;
  return () => {
    if (liberado) return;
    liberado = true;
    _trabajoEnCurso = Math.max(0, _trabajoEnCurso - 1);
  };
}

export function hayTrabajoEnCurso(): boolean {
  return _trabajoEnCurso > 0;
}

/**
 * Id del build publicado ahora mismo, o `null` si no se pudo saber.
 * `no-store` es imprescindible: con la respuesta cacheada, la app preguntaría
 * eternamente por una versión que ya cambió.
 */
export async function versionPublicada(): Promise<string | null> {
  try {
    const url = `${import.meta.env.BASE_URL}version.json?t=${Date.now()}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    const datos = (await res.json()) as { buildId?: unknown };
    return typeof datos.buildId === 'string' ? datos.buildId : null;
  } catch {
    // Sin red o servidor caído: no hay nada que actualizar y no es un error
    // que deba molestar a nadie. Se reintenta en la siguiente vuelta.
    return null;
  }
}

/** ¿Hay publicada una versión distinta de la que está corriendo? */
export async function hayVersionNueva(): Promise<boolean> {
  const publicada = await versionPublicada();
  return publicada !== null && publicada !== __BUILD_ID__;
}

/**
 * Arranca la vigilancia. Idempotente: llamarla dos veces no duplica temporizadores.
 *
 * `recargar` se inyecta para poder probar esto sin recargar el runner de tests.
 */
export function iniciarAutoActualizacion(
  recargar: () => void = () => window.location.reload(),
): void {
  if (_iniciado) return;
  _iniciado = true;

  const intentar = async () => {
    if (hayTrabajoEnCurso()) return;
    if (await hayVersionNueva()) recargar();
  };

  // Al volver a primer plano: el momento natural para aplicar la actualización.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void intentar();
  });

  // Y de vez en cuando, para la app que se queda abierta y visible durante horas.
  setInterval(() => { void intentar(); }, INTERVALO_MS);
}

/** Solo para pruebas: devuelve el módulo a su estado inicial. */
export function _reiniciarParaPruebas(): void {
  _trabajoEnCurso = 0;
  _iniciado = false;
}
